import React, { useState, useEffect } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Typography,
} from "@mui/material";
import CoreInputField from "../../../app/core/components/CoreInputField/CoreInputField";
import CoreButton from "../../../app/core/components/CoreButton/CoreButton";
import CoreSelectObjArr from "../../../app/core/components/CoreSelect/CoreSelectObjArr";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CoreAutoComplete from "../../../app/core/components/CoreAutoComplete/CoreAutoComplete";
import {
  useDoEditProfileMutation,
  useGetCitiesQuery,
} from "../../api/user/modificaProfiloApi";
import { t } from "i18next";

const FormUser = ({ user, employment }) => {
  const [formData, setFormData] = useState({
    nome: user?.name,
    cognome: user?.surname,
    email: user?.email,
    comune: {
      code: user?.common?.cadastralCode,
      nome: user?.common?.name,
      provincia: user?.common?.province,
    },
    cap: user?.cap,
    codiceFiscale: user?.fiscalCode,
    indirizzo: user?.address,
    occupazione: user?.employmentStatusId,
    nomeAzienda: user?.userCompany?.name,
  });

  const [check, setCheck] = useState("");
  const [errors, setErrors] = useState({});

  const [doEditProfile, { isLoading }] = useDoEditProfileMutation();

  const studioId = useSelector((state) => state.app.studioId);
  const appConfig = useSelector((state) => state.app.appConfig);

  const employmentOptions = employment?.map((el) => ({
    label: el?.name,
    value: el?.id,
  }));

  useEffect(() => {
    if (user) {
      setCheck(user.isDichCongiunta);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevData) => ({ ...prevData, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleInputChangeAuto = (event, newValue) => {
    setErrors((prevData) => ({ ...prevData, comune: "" }));
    setFormData((prevData) => ({
      ...prevData,
      comune: {
        code: newValue ? newValue?.cadastralCode : "",
        nome: newValue ? newValue?.name : "",
      },
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nome) newErrors.nome = t("FieldRequired");
    if (!formData.cognome) newErrors.cognome = t("FieldRequired");
    //if (!formData.occupazione) newErrors.occupazione = t("FieldRequired");
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleEditProfile = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    const bodyDataEditProfile = {
      Id: user.id,
      StudioId: studioId || null,
      Address: formData.indirizzo,
      CAP: formData.cap,
      FiscalCode: formData.codiceFiscale,
      EmploymentStatusId: formData.occupazione,
      Name: formData.nome,
      Surname: formData.cognome,
      Email: formData.email,
      IsDichCongiunta: check,
      ThirdParty: true,
    };
    if (formData.comune.code)
      bodyDataEditProfile.Common = {
        Name: formData.comune.nome,
        CadastralCode: formData.comune.code,
      };
    doEditProfile(bodyDataEditProfile).then((res) => {
      if (res?.data?.Result !== 1) {
        toast.error(res?.data?.Exception);
        return;
      }
      toast.success(`${t("Profile")} ${t("edited")}`);
    });
  };

  return (
    <div className="flex flex-col w-full">
      <FormControl>
        <CoreSelectObjArr
          error={errors.occupazione}
          label={t("Role")}
          name="occupazione"
          items={employmentOptions}
          onChange={handleInputChange}
          value={formData.occupazione}
          helperText={errors.occupazione}
        />
        {appConfig?.modules?.company?.general && (
          <CoreInputField
            disabled
            className="mt-6"
            label={t("AssociatedCompany")}
            value={formData.nomeAzienda || "N/A"}
          />
        )}

        <CoreInputField
          required
          error={errors.nome}
          className="mt-6"
          label={t("Name")}
          name="nome"
          onChange={handleInputChange}
          value={formData.nome}
          helperText={errors.nome}
        />

        <CoreInputField
          required
          error={errors.cognome}
          className="mt-6"
          label={t("Surname")}
          name="cognome"
          value={formData.cognome}
          onChange={handleInputChange}
          helperText={errors.cognome}
        />

        <div className="mt-10">
          <CoreAutoComplete
            name="comune"
            error={errors.comune}
            common={formData.comune.nome}
            label={t("City")}
            onChange={handleInputChangeAuto}
            fetchData={useGetCitiesQuery}
          />
          {errors.comune && (
            <Typography variant="caption" color="error">
              {t("FieldRequired")}
            </Typography>
          )}
        </div>

        <CoreInputField
          error={errors.cap}
          className="mt-6"
          label={t("PostalCode")}
          name="cap"
          value={formData.cap}
          onChange={handleInputChange}
          helperText={errors.cap}
        />

        <CoreInputField
          error={errors.indirizzo}
          className="mt-6"
          label={t("Address")}
          name="indirizzo"
          value={formData.indirizzo}
          onChange={handleInputChange}
          helperText={errors.indirizzo}
        />

        <CoreInputField
          error={errors.codiceFiscale}
          className="mt-6"
          label={t("CF")}
          name="codiceFiscale"
          value={formData.codiceFiscale}
          onChange={handleInputChange}
          helperText={errors.codiceFiscale}
        />

        <div className="w-full flex gap-1 items-end space-x-2">
          <CoreInputField
            disabled
            className="mt-6 w-full"
            label="Email"
            type="email"
            name="email"
            value={formData.email}
          />
          <CoreInputField
            disabled
            className="mt-6 w-full"
            label={t("Operator")}
            name="operator"
            value={user?.commDisplayName}
          />
        </div>

        {appConfig.modules.family && (
          <div className="flex justify-between mt-10">
            <h3 className="m-0">Dichiarazione congiunta?</h3>
            <div className="flex">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={check === true}
                    onChange={() => setCheck(true)}
                  />
                }
                label="Si"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={check === false}
                    onChange={() => setCheck(false)}
                  />
                }
                label="No"
              />
            </div>
          </div>
        )}

        <div className="flex justify-around mt-12">
          <CoreButton
            disabled={isLoading}
            sx={{ width: "150px", textAlign: "center" }}
            className={"btn-round"}
            variant="contained"
            testo={t("Save")}
            onClick={handleEditProfile}
          />
        </div>
      </FormControl>
    </div>
  );
};

export default FormUser;
